import { ExerciseSearchItem } from '../../api/exercises.interface';
import { ProgramCatalogEntry, ProgramCatalogItem, ProgramCompletionAction, ProgramResponse, ProgramVisibility, ProgramWeekSchedule, ProgramWeeks, ProgramWorkoutItemRecord } from '../../api/programs.interface';
import {
    CopyWeekFormErrors,
    CopyWeekFormValues,
    ProgramCreateFormErrors,
    ProgramCreateFormValues,
    ProgramDayCardSummary,
    ProgramDayEditorErrors,
    ProgramDayEditorValues,
    ProgramDeleteSummary,
    ProgramEditorDraft,
    ProgramManagementListItem,
    ProgramWeekOption,
    ProgramWeekStatistics,
} from './programs.types';

export const PROGRAM_DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7];

export const DEFAULT_PROGRAM_CREATE_VALUES: ProgramCreateFormValues = {
    completionAction: 'restart',
    description: '',
    displayName: '',
    imageUrl: '',
    name: '',
    nextProgramKey: '',
    sortOrder: '1',
    sourceProgramName: '',
    sourceWeek: '1',
    templateType: 'blank',
    visibility: 'draft',
    weeks: '1',
};

export const PROGRAM_COMPLETION_ACTION_LABELS: Record<ProgramCompletionAction, string> = {
    advance: 'Advance To Next Program',
    restart: 'Restart Current Program',
    stay: 'Stay On Current Program',
};

export const PROGRAM_VISIBILITY_LABELS: Record<ProgramVisibility, string> = {
    draft: 'Draft',
    internal: 'Internal',
    live: 'Live',
};

export const DEFAULT_COPY_WEEK_VALUES: CopyWeekFormValues = {
    overwriteExisting: false,
    sourceWeek: '1',
    targetWeek: '2',
};

export function normalizeProgramNameInput(name: string): string {
    return name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

export function buildProgramContentPath(programKey: string): string {
    return `/programs/${normalizeProgramNameInput(programKey)}`;
}

export function buildProgramSchedulePath(programKey: string): string {
    return `/schedule_v2/${normalizeProgramNameInput(programKey)}`;
}

export function createDefaultProgramCatalogItem(programName: string, totalWeeks = 1): ProgramCatalogItem {
    const normalizedProgramName = normalizeProgramNameInput(programName);
    const fallbackDisplayName = normalizedProgramName
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

    return {
        completionAction: 'restart',
        description: fallbackDisplayName
            ? `Start the ${fallbackDisplayName} program and continue through each workout week.`
            : `Start the ${programName.trim() || 'program'} plan and continue through each workout week.`,
        displayName: fallbackDisplayName || programName.trim(),
        imageUrl: undefined,
        visibility: 'draft',
        live: false,
        nextProgramKey: undefined,
        sortOrder: 1,
        weeks: Math.max(totalWeeks, 1),
    };
}

export function syncProgramCreateValuesWithName(values: ProgramCreateFormValues, name: string): ProgramCreateFormValues {
    const normalizedName = normalizeProgramNameInput(name);
    const nextDefaultCatalog = createDefaultProgramCatalogItem(normalizedName || values.name || 'program', Number(values.weeks) || 1);

    return {
        ...values,
        displayName: values.displayName || nextDefaultCatalog.displayName,
        name: normalizedName,
    };
}

export function syncProgramCreateValuesWithDisplayName(values: ProgramCreateFormValues, displayName: string): ProgramCreateFormValues {
    return {
        ...syncProgramCreateValuesWithName(values, displayName),
        displayName,
    };
}

export function cloneProgramWeeks(weeks: ProgramWeeks): ProgramWeeks {
    return JSON.parse(JSON.stringify(weeks)) as ProgramWeeks;
}

export function createProgramDraft(program: ProgramResponse): ProgramEditorDraft {
    return {
        catalog: createDefaultProgramCatalogItem(program.name, getProgramWeekNumbers(program.weeks).length || 1),
        name: program.name,
        weeks: cloneProgramWeeks(program.weeks),
    };
}

export function createBlankProgramWeeks(): ProgramWeeks {
    return {
        '1': {},
    };
}

export function countProgramDays(weeks: ProgramWeeks): number {
    return Object.values(weeks).reduce((total, weekSchedule) => total + Object.keys(weekSchedule).length, 0);
}

export function getProgramWeekNumbers(weeks: ProgramWeeks): number[] {
    return Object.keys(weeks)
        .map((week) => Number(week))
        .filter((week) => Number.isInteger(week) && week > 0)
        .sort((left, right) => left - right);
}

export function getProgramNextWeekNumber(weeks: ProgramWeeks): number {
    const weekNumbers = getProgramWeekNumbers(weeks);

    if (!weekNumbers.length) {
        return 1;
    }

    return weekNumbers[weekNumbers.length - 1] + 1;
}

export function getProgramWeekSchedule(weeks: ProgramWeeks, week: number): ProgramWeekSchedule {
    return weeks[String(week)] || {};
}

export function getProgramWeekStatistics(weeks: ProgramWeeks, week: number): ProgramWeekStatistics {
    const filledDays = Object.keys(getProgramWeekSchedule(weeks, week)).length;

    return {
        filledDays,
        totalDays: PROGRAM_DAY_NUMBERS.length,
    };
}

export function getProgramEmptyDayNumbers(weeks: ProgramWeeks, week: number): number[] {
    const weekSchedule = getProgramWeekSchedule(weeks, week);

    return PROGRAM_DAY_NUMBERS.filter((day) => !weekSchedule[String(day)]);
}

export function createProgramDraftFromResponse(program: ProgramResponse, catalog: ProgramCatalogItem): ProgramEditorDraft {
    return {
        catalog,
        name: program.name,
        weeks: cloneProgramWeeks(program.weeks),
    };
}

export function createProgramManagementListItem(programCatalogEntry: ProgramCatalogEntry): ProgramManagementListItem {
    const totalWeeks = programCatalogEntry.weeks;
    const totalDays = programCatalogEntry.totalConfiguredDays;
    const totalSlots = totalWeeks * PROGRAM_DAY_NUMBERS.length;
    const completionRatio = totalSlots ? totalDays / totalSlots : 0;
    let status = 'Needs attention';

    if (!totalDays) {
        status = 'Empty';
    } else if (completionRatio === 1) {
        status = 'Complete';
    } else if (completionRatio >= 0.75) {
        status = 'Near complete';
    } else if (completionRatio >= 0.4) {
        status = 'In progress';
    }

    return {
        completionAction: programCatalogEntry.completionAction,
        description: programCatalogEntry.description,
        displayName: programCatalogEntry.displayName,
        imageUrl: programCatalogEntry.imageUrl,
        visibility: programCatalogEntry.visibility,
        live: programCatalogEntry.live,
        name: programCatalogEntry.key,
        nextProgramKey: programCatalogEntry.nextProgramKey,
        schedulePath: buildProgramSchedulePath(programCatalogEntry.key),
        sortOrder: programCatalogEntry.sortOrder,
        status,
        totalDays,
        totalWeeks,
    };
}

export function createProgramDeleteSummary(program: ProgramResponse): ProgramDeleteSummary {
    return {
        totalDays: countProgramDays(program.weeks),
        totalWeeks: getProgramWeekNumbers(program.weeks).length,
    };
}

export function createProgramWeekOptions(weeks: ProgramWeeks): ProgramWeekOption[] {
    return getProgramWeekNumbers(weeks).map((week) => ({
        label: `Week ${week}`,
        value: week,
    }));
}

export function normalizeProgramSelectedWeek(weeks: ProgramWeeks, preferredWeek?: number): number {
    const availableWeeks = getProgramWeekNumbers(weeks);

    if (preferredWeek && availableWeeks.includes(preferredWeek)) {
        return preferredWeek;
    }

    if (preferredWeek && preferredWeek > 0 && !availableWeeks.length) {
        return preferredWeek;
    }

    if (availableWeeks.length) {
        return availableWeeks[0];
    }

    return 1;
}

export function ensureProgramWeek(weeks: ProgramWeeks, week: number): ProgramWeeks {
    const nextWeeks = cloneProgramWeeks(weeks);

    if (!nextWeeks[String(week)]) {
        nextWeeks[String(week)] = {};
    }

    return nextWeeks;
}

export function upsertProgramDayInDraft(
    weeks: ProgramWeeks,
    week: number,
    day: number,
    item: ProgramWorkoutItemRecord,
): ProgramWeeks {
    const nextWeeks = ensureProgramWeek(weeks, week);

    nextWeeks[String(week)][String(day)] = item;
    return nextWeeks;
}

export function deleteProgramDayFromDraft(weeks: ProgramWeeks, week: number, day: number): ProgramWeeks {
    const nextWeeks = ensureProgramWeek(weeks, week);

    delete nextWeeks[String(week)][String(day)];
    return nextWeeks;
}

export function copyProgramWeekInDraft(weeks: ProgramWeeks, sourceWeek: number, targetWeek: number): ProgramWeeks {
    const nextWeeks = ensureProgramWeek(weeks, targetWeek);
    const sourceSchedule = getProgramWeekSchedule(weeks, sourceWeek);

    nextWeeks[String(targetWeek)] = Object.entries(sourceSchedule).reduce<ProgramWeekSchedule>((accumulator, [dayKey, item]) => {
        accumulator[dayKey] = {
            ...item,
            week: targetWeek,
        };

        return accumulator;
    }, {});

    return nextWeeks;
}

export function createProgramDayEditorValues(item?: ProgramWorkoutItemRecord): ProgramDayEditorValues {
    return {
        description: item?.description || '',
        imageUrl: item?.imageUrl || '',
        level: item?.level || '',
        primaryType: item?.primaryType || '',
        rounds: item?.rounds !== undefined ? String(item.rounds) : '',
        secondaryType: item?.secondaryType || '',
        time: item?.time || '',
    };
}

export function getProgramWorkoutItemValidationMessages(item?: ProgramWorkoutItemRecord): string[] {
    if (!item) {
        return [];
    }

    const messages: string[] = [];

    if (!item.description.trim()) {
        messages.push('Missing description');
    }

    if (!item.imageUrl.trim()) {
        messages.push('Missing image URL');
    }

    if (!item.level.trim()) {
        messages.push('Missing level');
    }

    if (!item.primaryType.trim()) {
        messages.push('Missing primary type');
    }

    if (item.rounds !== undefined && (!Number.isInteger(item.rounds) || item.rounds < 1)) {
        messages.push('Invalid rounds');
    }

    return messages;
}

export function validateProgramDraftBeforeSave(draftProgram: ProgramEditorDraft): string[] {
    const issues: string[] = [];

    if (!draftProgram.name.trim()) {
        issues.push('Program name is required.');
    }

    if (!draftProgram.catalog.displayName.trim()) {
        issues.push('Display name is required.');
    }

    if (!draftProgram.catalog.description.trim()) {
        issues.push('Description is required.');
    }

    if (!Number.isInteger(draftProgram.catalog.weeks) || draftProgram.catalog.weeks < 1) {
        issues.push('Weeks must be a positive whole number.');
    }

    if (!Number.isInteger(draftProgram.catalog.sortOrder) || draftProgram.catalog.sortOrder < 1) {
        issues.push('Sort order must be a positive whole number.');
    }

    if (!['restart', 'advance', 'stay'].includes(draftProgram.catalog.completionAction)) {
        issues.push('Completion action is required.');
    }

    if (draftProgram.catalog.completionAction === 'advance' && !draftProgram.catalog.nextProgramKey?.trim()) {
        issues.push('Next program is required when completion action is set to advance.');
    }

    const configuredWeeks = getProgramWeekNumbers(draftProgram.weeks);

    if (configuredWeeks.length && draftProgram.catalog.weeks < configuredWeeks[configuredWeeks.length - 1]) {
        issues.push(`Weeks must be at least ${configuredWeeks[configuredWeeks.length - 1]} to cover the configured schedule.`);
    }

    Object.entries(draftProgram.weeks)
        .sort(([leftWeek], [rightWeek]) => Number(leftWeek) - Number(rightWeek))
        .forEach(([weekKey, weekSchedule]) => {
            Object.entries(weekSchedule)
                .sort(([leftDay], [rightDay]) => Number(leftDay) - Number(rightDay))
                .forEach(([dayKey, item]) => {
                    getProgramWorkoutItemValidationMessages(item).forEach((message) => {
                        issues.push(`Week ${weekKey} Day ${dayKey}: ${message}.`);
                    });
                });
        });

    return issues;
}

export function validateProgramCreateForm(values: ProgramCreateFormValues, existingProgramNames: string[]): ProgramCreateFormErrors {
    const errors: ProgramCreateFormErrors = {};
    const normalizedName = normalizeProgramNameInput(values.name);
    const normalizedNextProgramKey = normalizeProgramNameInput(values.nextProgramKey);
    const parsedWeeks = Number(values.weeks);
    const parsedSortOrder = Number(values.sortOrder);

    if (!normalizedName) {
        errors.name = 'Program name is required.';
    }

    if (!values.displayName.trim()) {
        errors.displayName = 'Display name is required.';
    }

    if (!values.description.trim()) {
        errors.description = 'Description is required.';
    }

    if (normalizedName && existingProgramNames.includes(normalizedName)) {
        errors.name = 'Program name must be unique.';
    }

    if (!Number.isInteger(parsedWeeks) || parsedWeeks < 1) {
        errors.weeks = 'Weeks must be a positive whole number.';
    }

    if (!Number.isInteger(parsedSortOrder) || parsedSortOrder < 1) {
        errors.sortOrder = 'Sort order must be a positive whole number.';
    }

    if (values.completionAction === 'advance' && !normalizedNextProgramKey) {
        errors.nextProgramKey = 'Choose the next program when the completion action is advance.';
    }

    if (values.completionAction === 'advance' && normalizedNextProgramKey && normalizedNextProgramKey === normalizedName) {
        errors.nextProgramKey = 'A program cannot advance to itself.';
    }

    if (values.templateType !== 'blank' && !values.sourceProgramName) {
        errors.sourceProgramName = 'Choose a source program.';
    }

    if (values.templateType === 'copyWeek') {
        const sourceWeek = Number(values.sourceWeek);

        if (!Number.isInteger(sourceWeek) || sourceWeek < 1) {
            errors.sourceWeek = 'Choose a valid source week.';
        }
    }

    return errors;
}

export function validateProgramDayEditor(values: ProgramDayEditorValues): ProgramDayEditorErrors {
    const errors: ProgramDayEditorErrors = {};

    if (!values.description.trim()) {
        errors.description = 'Description is required.';
    }

    if (!values.imageUrl.trim()) {
        errors.imageUrl = 'Image URL is required.';
    }

    if (!values.level.trim()) {
        errors.level = 'Level is required.';
    }

    if (!values.primaryType.trim()) {
        errors.primaryType = 'Primary type is required.';
    }

    if (values.rounds.trim()) {
        const rounds = Number(values.rounds);

        if (!Number.isInteger(rounds) || rounds < 1) {
            errors.rounds = 'Rounds must be a positive whole number.';
        }
    }

    return errors;
}

export function parseProgramTagString(value?: string): number[] {
    if (!value) {
        return [];
    }

    return value
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((part) => Number.isInteger(part) && part > 0);
}

export function getProgramWorkoutItemTagNumbers(item?: ProgramWorkoutItemRecord): number[] {
    return [
        ...parseProgramTagString(item?.primaryTag),
        ...parseProgramTagString(item?.secondaryTag),
    ];
}

export function getProgramWeekTagNumbers(weeks: ProgramWeeks, week: number): number[] {
    const weekSchedule = getProgramWeekSchedule(weeks, week);

    return Array.from(
        new Set(
            Object.values(weekSchedule).flatMap((item) => getProgramWorkoutItemTagNumbers(item)),
        ),
    );
}

export function createProgramTagNameLookup(items: ExerciseSearchItem[]): Record<number, string> {
    return items.reduce<Record<number, string>>((accumulator, item) => {
        accumulator[item.tag] = item.exercise;
        return accumulator;
    }, {});
}

export function createProgramTagDisplayLabels(
    value: string | undefined,
    tagNamesByTag: Record<number, string>,
): string[] {
    return parseProgramTagString(value).map((tag) => tagNamesByTag[tag] || `Tag ${tag}`);
}

export function createVisibleProgramTagDisplayLabels(
    value: string | undefined,
    tagNamesByTag: Record<number, string>,
    maxVisible = 2,
): { hiddenCount: number; labels: string[] } {
    const labels = createProgramTagDisplayLabels(value, tagNamesByTag);

    return {
        hiddenCount: Math.max(labels.length - maxVisible, 0),
        labels: labels.slice(0, maxVisible),
    };
}

export function serializeProgramExerciseTags(items: ExerciseSearchItem[]): string | undefined {
    if (!items.length) {
        return undefined;
    }

    return items.map((item) => item.tag).join(', ');
}

export function createExerciseSelectionPlaceholder(tag: number): ExerciseSearchItem {
    return {
        bootyBand: '',
        description: '',
        exercise: `Tag ${tag}`,
        index: tag,
        level: '',
        mat: '',
        plan: '',
        reps: '',
        step: '',
        tag,
        videoUrl: '',
        weight: '',
        weightRange: '',
    };
}

export function createProgramWorkoutItem(
    values: ProgramDayEditorValues,
    week: number,
    day: number,
    primaryExercises: ExerciseSearchItem[],
    secondaryExercises: ExerciseSearchItem[],
): ProgramWorkoutItemRecord {
    const roundsValue = values.rounds.trim();

    return {
        day,
        description: values.description.trim(),
        imageUrl: values.imageUrl.trim(),
        level: values.level.trim(),
        primaryTag: serializeProgramExerciseTags(primaryExercises),
        primaryType: values.primaryType.trim(),
        rounds: roundsValue ? Number(roundsValue) : undefined,
        secondaryTag: serializeProgramExerciseTags(secondaryExercises),
        secondaryType: values.secondaryType.trim() || undefined,
        time: values.time.trim() || undefined,
        week,
    };
}

export function createProgramDayCardSummary(item?: ProgramWorkoutItemRecord): ProgramDayCardSummary {
    return {
        primaryTags: parseProgramTagString(item?.primaryTag).map((tag) => String(tag)),
        secondaryTags: parseProgramTagString(item?.secondaryTag).map((tag) => String(tag)),
    };
}

export function getProgramWeekFilledDaysLabel(weeks: ProgramWeeks, week: number): string {
    const statistics = getProgramWeekStatistics(weeks, week);

    return `${statistics.filledDays}/${statistics.totalDays} configured`;
}

export function validateCopyWeekForm(values: CopyWeekFormValues, weeks: ProgramWeeks): CopyWeekFormErrors {
    const errors: CopyWeekFormErrors = {};
    const sourceWeek = Number(values.sourceWeek);
    const targetWeek = Number(values.targetWeek);

    if (!Number.isInteger(sourceWeek) || sourceWeek < 1) {
        errors.sourceWeek = 'Choose a valid source week.';
    }

    if (!Number.isInteger(targetWeek) || targetWeek < 1) {
        errors.targetWeek = 'Choose a valid target week.';
    }

    if (!Object.keys(getProgramWeekSchedule(weeks, sourceWeek)).length) {
        errors.sourceWeek = 'Source week must contain at least one configured day.';
    }

    if (sourceWeek === targetWeek) {
        errors.targetWeek = 'Choose a different target week.';
    }

    if (Object.keys(getProgramWeekSchedule(weeks, targetWeek)).length && !values.overwriteExisting) {
        errors.overwriteExisting = 'Confirm overwrite to replace an existing target week.';
    }

    return errors;
}

export function updateProgramCatalogItem(
    catalog: ProgramCatalogItem,
    fieldName: keyof ProgramCatalogItem,
    value: boolean | number | string,
): ProgramCatalogItem {
    if (fieldName === 'visibility') {
        const visibility = value as ProgramVisibility;

        return {
            ...catalog,
            visibility,
            live: visibility === 'live',
        };
    }

    if (fieldName === 'live') {
        const live = Boolean(value);

        return {
            ...catalog,
            live,
            visibility: live ? 'live' : 'draft',
        };
    }

    if (fieldName === 'completionAction') {
        return {
            ...catalog,
            completionAction: value as ProgramCompletionAction,
            nextProgramKey: value === 'advance' ? catalog.nextProgramKey : undefined,
        };
    }

    if (fieldName === 'imageUrl' || fieldName === 'nextProgramKey') {
        const trimmedValue = String(value).trim();

        return {
            ...catalog,
            [fieldName]: trimmedValue || undefined,
        } as ProgramCatalogItem;
    }

    return {
        ...catalog,
        [fieldName]: value,
    } as ProgramCatalogItem;
}